import CryptoJS from 'crypto-js'
import type { BaseApiModule, CommunityVideoCategory, CommunityVideoInfo } from '~/server/types'
import type { CatalogsConfig } from '~/components/gfr/types/components'
const md5Solt = 'vK9mP2xL5nQ8wR4tY7jH3fG6bN1zA0sD'

const componentNameMap = {
  ModuleCommonEvents: 'events',
  ModuleCommonMaps: 'maps'
}
/**
 * Like item id storage management
 * Local storage key for community liked items
 */
const COMMUNITY_VIDEO_LIKE_KEY = 'communityVideoLikedIds'

/**
 * 获取本地已like的视频id数组（去重）
 * @returns string[] 已like的item id列表，保证唯一
 */
function getLikedVideoIds(): string[] {
  const val = storage.getItem(COMMUNITY_VIDEO_LIKE_KEY)
  if (!val) return []
  try {
    const arr = JSON.parse(val)
    return Array.isArray(arr) ? Array.from(new Set(arr)) : []
  } catch (e) {
    return []
  }
}

/**
 * 设置本地已like视频id数组，自动去重
 * @param ids string[]
 */
function setLikedVideoIds(ids: string[]) {
  const uniqueIds = Array.from(new Set(ids))
  storage.setItem(COMMUNITY_VIDEO_LIKE_KEY, JSON.stringify(uniqueIds))
}

/**
 * 添加like的item id（去重）
 * @param itemId string
 */
function addLikedVideoId(itemId: string) {
  const ids = getLikedVideoIds()
  if (!ids.includes(itemId)) {
    ids.push(itemId)
    setLikedVideoIds(ids)
  }
}

/**
 * 移除like的item id
 * @param itemId string
 */
function removeLikedVideoId(itemId: string) {
  let ids = getLikedVideoIds()
  ids = ids.filter((id) => id !== itemId)
  setLikedVideoIds(ids)
}

/**
 * 检查给定item id是否已经被like
 * @param itemId string
 * @returns boolean
 */
function isLikedVideoId(itemId: string): boolean {
  return getLikedVideoIds().includes(itemId)
  // return true
}

/**
 * MD5 加密方法
 * @param text 需要加密的字符串
 * @returns 加密后的 MD5 哈希值（小写）
 */
function md5(text: string): string {
  return CryptoJS.MD5(text).toString()
}

const signRequest = (body: any): any => {
  /** 时间戳 */
  const _ts = parseInt(`${Date.parse(new Date().toISOString()) / 1000}`)
  /** key=value */
  const _keyValue = Object.entries(body)
    .map(([key, value]) => `${key}=${value}`)
    .join('')

  const _sign = md5(`${md5Solt}_ts=${_ts}${_keyValue}`)
  return {
    ...body,
    _sign,
    _ts
  }
}

export const useCommunityStore = defineStore('Community', () => {
  const route = useRoute()
  const pageStore = usePageStore()
  const { modules, pathData } = storeToRefs(pageStore)

  const videoCategoryList = ref<CommunityVideoCategory[]>([])
  const showMenu = ref(false)
  async function fetchCommunityData() {
    await pageStore.fetchPageData('/cl-community/')
    // 获取视频分类列表
    if (pathData.value?.videos) {
      videoCategoryList.value = pathData.value.videos
    }
  }
  function toggleCommunityMenu() {
    showMenu.value = !showMenu.value
    document.body.classList.toggle('overflow-hidden')
    document.body.classList.toggle('pointer-events-none')
  }
  function weightListSort(list: any[]) {
    return list.filter((item: any) => item.display === 1).sort((a: any, b: any) => b.weight - a.weight)
  }
  function formateKey(key: string) {
    return key.trim().toLowerCase().replace(/\s+/g, '-')
  }
  function isVideoCategory(key: string) {
    return videoCategoryList.value.some((item) => formateKey(item.name) === key)
  }
  function filterCommunityVideo(
    list: CommunityVideoInfo[],
    condition: (item: CommunityVideoInfo, index: number) => boolean
  ) {
    return list.filter(condition)
  }
  function searchByName(text: string) {
    return (item: CommunityVideoInfo) => item.title.toLowerCase().includes(text.trim().toLowerCase())
  }
  function searchByTag(tag?: string) {
    return (item: CommunityVideoInfo) => (tag ? !!(tag === 'recommended' && item.tag) : true)
  }
  function updateVideoLike() {
    if (!Array.isArray(videoCategoryList.value)) return []
    videoCategoryList.value.forEach((category) => {
      const videos = category.videos
      videos.forEach((video) => {
        video.isLike = isLikedVideoId(video.id.toString())
      })
    })
    return videoCategoryList
  }

  /**
   * 格式化点赞数显示规则：
   * - 点赞数为 0：不显示（返回空字符串）
   * - 0 < n < 1000：显示对应点赞数值
   * - 1000 <= n < 10000：以 k 为单位，保留一位小数向下取整（如 1234 → 1.2k）
   * - 10000 <= n < 100000：以 k 为单位，保留个位向下取整（如 12345 → 12k）
   * - n >= 100000：统一显示 99k+
   */
  function formateLikeNum(likeCount: number): string {
    likeCount = Number(likeCount)
    if (Number.isNaN(likeCount) || likeCount <= 0) return '0'
    if (likeCount < 1000) return String(likeCount)
    if (likeCount < 10000) {
      const num = Math.floor(likeCount / 100) / 10
      return `${num.toFixed(1)}k`
    }
    if (likeCount < 100000) {
      const num = Math.floor(likeCount / 1000)
      return `${num}k`
    }
    return '99k+'
  }

  const requestLoading = ref(false)
  async function videoLikeHandler(item: CommunityVideoInfo) {
    if (requestLoading.value) return
    requestLoading.value = true
    async function likeOption() {
      await fetchVideoLike('like', item.id.toString())
      item.like++
      item.isLike = true
      addLikedVideoId(item.id.toString())
    }
    async function unlikeOption() {
      await fetchVideoLike('dislike', item.id.toString())
      item.isLike = false
      item.like = item.like < 0 ? 0 : item.like - 1
      removeLikedVideoId(item.id.toString())
    }
    try {
      item.isLike ? await unlikeOption() : await likeOption()
      // updateVideoLike()
    } finally {
      requestLoading.value = false
    }
  }

  //  Menu Data Map
  const communityPageDataMap = computed(() => {
    const map = new Map<string, Array<BaseApiModule['detail'] | CommunityVideoInfo>>()
    // videoCategoryList: name -> videos

    for (const category of videoCategoryList.value) {
      if (category.name && Array.isArray(category.videos)) {
        map.set(formateKey(category.name), category.videos)
      }
    }
    // modules: title -> events
    for (const module of modules.value) {
      const titleComponentName = module.component_name as string
      const eventsName = componentNameMap[titleComponentName as keyof typeof componentNameMap] || ''
      const events = module.detail?.[eventsName.toLowerCase()]
      const title = module.detail?.title

      if (typeof eventsName === 'string' && Array.isArray(events)) {
        map.set(formateKey(title), weightListSort(events))
      }
    }

    return map
  })
  //  Side Menu LIst
  const communityMenu = computed(() => {
    const keys = Array.from(communityPageDataMap.value.keys())
    return keys.map((key, index) => {
      const url = formateKey(key)
      return {
        id: index + 1,
        title: key,
        url,
        open: true,
        obsoleted: false
      } satisfies CatalogsConfig
    })
  })

  async function fetchVideoLike(likeState: 'like' | 'dislike', id: string) {
    await $fetch(`/api/video-${likeState}`, {
      method: 'POST',
      body: signRequest({
        id
      })
    })
  }

  return {
    showMenu,
    toggleCommunityMenu,
    fetchCommunityData,
    filterCommunityVideo,
    searchByTag,
    searchByName,
    formateLikeNum,
    videoLikeHandler,
    updateVideoLike,
    md5,
    isVideoCategory,
    modules,
    videoCategoryList,
    communityPageDataMap,
    communityMenu
  }
})
